import { FC, Fragment } from 'react'
import { useLocation } from 'react-router-dom'

import { ButtonPromo, TextPromo } from 'components/ui'
import { Href } from 'components/ui/Href/Href'
import { Flex } from 'components/uiKit'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useResize } from 'utils/hooks/useResize'
import { docs } from 'shared/constants/docs'
import { PromoRoutesEnum } from 'shared/constants/routes'
import { EventType } from 'types/types'

import { labels } from '../../data'
import { ConditionDesc, IComparePPData, pickpointType } from '../../types'

import s from './PickPointBox.module.scss'

interface IPickPointBoxProps {
  data: IComparePPData
  title: string
  typeOfPickpoint: pickpointType.standard | pickpointType.simplified
}

export const PickPointBox: FC<IPickPointBoxProps> = ({ data, title, typeOfPickpoint }) => {
  const { isMobile, isTablet } = useResize()
  const location = useLocation()

  const fontSize = isMobile ? 's16' : 's20'
  const buttonFontSize = isTablet ? 's16' : 's20'
  const isSimplifiedPage = location.pathname === PromoRoutesEnum.simplifiedConditions
  const isMainPage = location.pathname === PromoRoutesEnum.root

  const getButtonConfig = (): {
    text: string
    link: string
    variant: 'purple' | 'pink' | 'white' | 'arrow' | 'darkGray'
    linkType: 'search' | 'pathname'
    target: '_blank' | '_self'
    event: EventType.ConditionsDetailsT | EventType.BrandbookDnl
    eventType: 'Standard' | 'Simplified'
  } => {
    if (isSimplifiedPage) {
      return {
        text: typeOfPickpoint === pickpointType.standard ? 'Подробнее' : 'Скачать брендбук',
        link: typeOfPickpoint === pickpointType.standard ? PromoRoutesEnum.root : docs.brandbookSimplified,
        variant: typeOfPickpoint === pickpointType.standard ? 'darkGray' : 'purple',
        linkType: typeOfPickpoint === pickpointType.standard ? 'pathname' : 'search',
        target: typeOfPickpoint === pickpointType.standard ? '_self' : '_blank',
        event: typeOfPickpoint === pickpointType.standard ? EventType.ConditionsDetailsT : EventType.BrandbookDnl,
        eventType: 'Standard',
      }
    }
    return {
      text: typeOfPickpoint === pickpointType.standard ? 'Скачать брендбук' : 'Подробнее',
      link: typeOfPickpoint === pickpointType.standard ? docs.brandbook : PromoRoutesEnum.simplifiedConditions,
      variant: typeOfPickpoint === pickpointType.standard ? 'purple' : 'darkGray',
      linkType: typeOfPickpoint === pickpointType.standard ? 'search' : 'pathname',
      target: typeOfPickpoint === pickpointType.standard ? '_blank' : '_self',
      event: typeOfPickpoint === pickpointType.standard ? EventType.BrandbookDnl : EventType.ConditionsDetailsT,
      eventType: 'Simplified',
    }
  }

  const { text, link, variant, linkType, target, event, eventType } = getButtonConfig()

  return (
    <div
      className={s.correlationPPBox}
      style={
        (isSimplifiedPage && typeOfPickpoint === pickpointType.simplified) ||
        (isMainPage && typeOfPickpoint === pickpointType.standard)
          ? { border: '3px solid #7635DB' }
          : undefined
      }
    >
      <TextPromo
        size={isMobile ? 's22' : !isTablet ? 's44' : 's32'}
        fontFamily="ASLHaussVF"
        center
        weight="w550"
        className={s.title}
      >
        {title}
      </TextPromo>
      <Flex vertical gap={isMobile ? 12 : 16}>
        {data.conditions.map((item, index) => (
          <Flex key={item.id} vertical className={s.conditionItem}>
            {index === 0 && <img src={data.preview} alt="pp-preview" className={s.preview} loading="lazy" />}
            <Flex vertical className={s.conditionItemWrapper} gap={8}>
              <TextPromo
                size={isMobile ? 's14' : 's16'}
                fontFamily="ASLHauss"
                className={s.conditionLabel}
                color="gray"
              >
                {labels[item.labelKey]}
              </TextPromo>
              <TextPromo size={fontSize} fontFamily="ASLHauss" className={s.conditionText}>
                {item.text.map((descriptionPart: ConditionDesc, idx) => {
                  let content
                  if (typeof descriptionPart === 'object') {
                    content = (
                      <Href key={`link-${idx}`} href={descriptionPart[0]} className={s.link} target="_blank">
                        {descriptionPart[1]}
                      </Href>
                    )
                  } else {
                    content = (
                      <Fragment key={`text-${idx}`}>
                        <TextPromo key={`text-${idx}`} size={fontSize} fontFamily="ASLHauss">
                          {descriptionPart}
                        </TextPromo>
                      </Fragment>
                    )
                  }
                  return content
                })}
              </TextPromo>
            </Flex>
          </Flex>
        ))}
        <ButtonPromo
          fullWidth
          className={s.button}
          variant={variant}
          onClick={() => {
            sendEvent(event, { type: eventType })
          }}
        >
          <Href href={link} type="text" className={(s.link, s.buttonLink)} linkType={linkType} target={target}>
            <TextPromo fontFamily="ASLHaussVF" size={buttonFontSize} color="white" pointer>
              {text}
            </TextPromo>
          </Href>
        </ButtonPromo>
      </Flex>
    </div>
  )
}
